import { shiftDate } from '../format/dates'
import { csvText } from './csv'
import type { StockItem, StockLevels, StockLocation, StockMovement, StockMovementInput, StockPeriod } from '../types/stock'

/**
 * Rekenregels voor voorraad per plek (docs/VOORRAAD.md). Puur en getest; de server rekent hetzelfde.
 *
 * - De webshop kijkt alleen naar `webshop` (= `Product.stock`). Salonvoorraad maakt een product
 *   online nooit leverbaar.
 * - De kassa boekt af van `salon`. Op 0 waarschuwt het scherm ("volgens de telling op"), maar de
 *   verkoop gaat door: de telling kan fout zijn, en de klant staat met het product aan de balie.
 *   De stand mag dan negatief worden; de volgende telling zet hem recht.
 * - Elke verandering is een mutatie; de stand is altijd de som ervan plus de beginstand.
 */

export const MAX_MOVEMENT_QTY = 999

export const reasonLabel: Record<StockMovement['reason'], string> = {
  'verkoop-webshop': 'Verkocht (webshop)',
  'verkoop-salon': 'Verkocht (salon)',
  verbruik: 'In gebruik genomen',
  overboeken: 'Overgeboekt',
  inkoop: 'Inkoop ontvangen',
  afschrijven: 'Afgeschreven',
  telling: 'Telling',
}

export const locationLabel: Record<StockLocation, string> = { webshop: 'Webshop', salon: 'Salon' }

/**
 * Pas één mutatie toe. Een plek die niet bijgehouden wordt (`null`) blijft `null`, behalve als er
 * aantoonbaar iets binnenkomt: een telling, een inkoop, of een overboeking *naar* die plek. Iets
 * weghalen van een niet-bijgehouden plek start hem niet (dat zou een verzonnen 0 zijn).
 */
export function applyMovement(levels: StockLevels, m: Pick<StockMovement, 'location' | 'qty' | 'reason'>): StockLevels {
  const current = levels[m.location]
  if (current === null) {
    const starts = m.reason === 'telling' || m.reason === 'inkoop' || (m.reason === 'overboeken' && m.qty > 0)
    return starts ? { ...levels, [m.location]: Math.max(0, m.qty) } : levels
  }
  return { ...levels, [m.location]: current + m.qty }
}

const positiveInt = (n: number) => Number.isInteger(n) && n > 0 && n <= MAX_MOVEMENT_QTY

/** Wat er niet klopt aan een verzoek, of `null`. Overboeken mag niet meer dan er ligt (anders klopt de telling niet). */
export function movementProblem(req: StockMovementInput, levels: StockLevels): string | null {
  switch (req.type) {
    case 'overboeken': {
      if (req.from === req.to) return 'Kies twee verschillende plekken.'
      if (!positiveInt(req.qty)) return 'Vul een aantal in.'
      const there = levels[req.from]
      // Niet bijgehouden = onbekend hoeveel er ligt: overboeken zou voorraad uit het niets maken.
      if (there === null) return `Op ${locationLabel[req.from].toLowerCase()} wordt geen voorraad bijgehouden. Tel daar eerst, dan kun je overboeken.`
      if (there < req.qty) return `Er ${there === 1 ? 'ligt' : 'liggen'} er maar ${Math.max(0, there)} op die plek.`
      return null
    }
    case 'verbruik':
    case 'inkoop':
    case 'afschrijven':
      if (!positiveInt(req.qty)) return 'Vul een aantal in.'
      if (req.type === 'inkoop' && req.costPriceCents !== undefined && (!Number.isInteger(req.costPriceCents) || req.costPriceCents < 0)) return 'Vul een geldige inkoopprijs in.'
      return null
    case 'telling':
      if (!Number.isInteger(req.counted) || req.counted < 0 || req.counted > 99_999) return 'Vul het getelde aantal in (0 of meer).'
      return null
  }
}

/** De mutaties (zonder id/at/by) die een verzoek oplevert. Een telling zonder verschil levert niets op. */
export function movementsFor(req: StockMovementInput, levels: StockLevels): Omit<StockMovement, 'id' | 'at' | 'by' | 'itemId'>[] {
  const note = req.note?.trim() || undefined
  switch (req.type) {
    case 'overboeken':
      return [
        { reason: 'overboeken', location: req.from, qty: -req.qty, note },
        { reason: 'overboeken', location: req.to, qty: req.qty, note },
      ]
    case 'verbruik':
      return [{ reason: 'verbruik', location: req.location, qty: -req.qty, note }]
    case 'inkoop':
      return [{ reason: 'inkoop', location: req.location, qty: req.qty, note }]
    case 'afschrijven':
      return [{ reason: 'afschrijven', location: req.location, qty: -req.qty, note, writeOffCause: req.cause }]
    case 'telling': {
      const current = levels[req.location]
      const diff = req.counted - (current ?? 0)
      // Van "niet bijhouden" naar een getal: ook bij 0 een regel, zodat te zien is wanneer het begon.
      if (diff === 0 && current !== null) return []
      return [{ reason: 'telling', location: req.location, qty: current === null ? req.counted : diff, note }]
    }
  }
}

/** Totaal over alle plekken die bijgehouden worden. */
export const totalStock = (levels: StockLevels) => Object.values(levels).reduce<number>((s, v) => s + (v ?? 0), 0)

/** Op volgens de telling (0 of minder), maar wel bijgehouden. */
export const isOutAt = (levels: StockLevels, location: StockLocation) => {
  const v = levels[location]
  return v !== null && v <= 0
}

/** Netto verkocht per kanaal sinds `since` (inclusief): een correctie of annulering telt terug. */
export function soldSince(movements: readonly StockMovement[], itemId: string, since: string): Record<StockLocation, number> {
  const sold = { webshop: 0, salon: 0 }
  movements.forEach((m) => {
    if (m.itemId !== itemId || m.at.slice(0, 10) < since) return
    if (m.reason === 'verkoop-webshop') sold.webshop -= m.qty
    if (m.reason === 'verkoop-salon') sold.salon -= m.qty
  })
  return sold
}

export const sold30Since = (today: string) => shiftDate(today, -29)

/** Voorraad deze periode: verkocht per kanaal, verbruik en afgeschreven, in stuks en tegen inkoopprijs. */
export function stockPeriod(movements: readonly StockMovement[], from: string, to: string): StockPeriod {
  const out: StockPeriod = {
    soldWebshop: { qty: 0, costCents: 0 },
    soldSalon: { qty: 0, costCents: 0 },
    used: { qty: 0, costCents: 0 },
    writtenOff: { qty: 0, costCents: 0 },
  }
  const key: Partial<Record<StockMovement['reason'], keyof StockPeriod>> = {
    'verkoop-webshop': 'soldWebshop',
    'verkoop-salon': 'soldSalon',
    verbruik: 'used',
    afschrijven: 'writtenOff',
  }
  movements.forEach((m) => {
    const day = m.at.slice(0, 10)
    const k = key[m.reason]
    if (!k || day < from || day > to) return
    out[k] = { qty: out[k].qty - m.qty, costCents: out[k].costCents - m.qty * (m.costPriceCents ?? 0) }
  })
  return out
}

/** Een item met zijn verkopen van de laatste 30 dagen erbij. */
export const withSold30 = (item: Omit<StockItem, 'sold30'>, movements: readonly StockMovement[], today: string): StockItem => ({
  ...item,
  sold30: soldSince(movements, item.id, sold30Since(today)),
})

// ——— Export ———

// Gedeeld met de andere exports: ook geen formules uit een notitie of naam (CSV-injectie).
const csvCell = csvText
const csvMoney = (cents: number) => `${cents < 0 ? '-' : ''}${Math.floor(Math.abs(cents) / 100)},${String(Math.abs(cents) % 100).padStart(2, '0')}`

/** GET /admin/reports/stock-movements.csv — alle mutaties in de periode, één per regel. */
export function movementsCsv(movements: readonly StockMovement[], nameOf: (itemId: string) => string, from: string, to: string): string {
  const header = ['Datum', 'Tijd', 'Artikel', 'Reden', 'Plek', 'Aantal', 'Inkoopprijs per stuk', 'Waarde inkoop', 'Referentie', 'Notitie', 'Door']
  const rows = movements
    .filter((m) => m.at.slice(0, 10) >= from && m.at.slice(0, 10) <= to)
    .sort((a, b) => a.at.localeCompare(b.at))
    .map((m) =>
      [
        m.at.slice(0, 10),
        m.at.slice(11, 16),
        nameOf(m.itemId),
        reasonLabel[m.reason] + (m.writeOffCause ? ` (${m.writeOffCause})` : ''),
        locationLabel[m.location],
        String(m.qty),
        m.costPriceCents === undefined ? '' : csvMoney(m.costPriceCents),
        m.costPriceCents === undefined ? '' : csvMoney(m.qty * m.costPriceCents),
        m.ref ?? '',
        m.note ?? '',
        m.by,
      ]
        .map(csvCell)
        .join(';'),
    )
  return [header.join(';'), ...rows].join('\r\n')
}
